/**
 * EventLog — the chronological store of everything that happened in
 * a run. The single source of truth feeding the Spine's cumulative
 * badges (P3), the Timeline view (P4), command filters (P5), replay
 * (P6a), export (P6b), and UI-state undo (P6c).
 *
 * Spec 17 §4.1 — see docs/specs/17-variant-c-timeline-phases-and-grammar.md.
 *
 * Core invariants (enforced by the append function + TypeScript):
 *   * Entries are strictly time-ordered by insertion. The adapter
 *     appends as events arrive; no out-of-order inserts.
 *   * Entries are immutable once written. Views read; nothing mutates.
 *   * kind is a closed enum. An unknown kind from the adapter is
 *     logged + dropped at the boundary, never appended (fail-fast).
 *   * Every mutation produces a NEW log instance (structural sharing
 *     where cheap, full rebuild where simpler). Callers get the new
 *     log back; the input is never touched.
 *
 * SRP: pure data + pure functions. No React, no side effects, no
 * I/O. The reducer owns the ``EventLog`` as state and calls this
 * module's functions to update it.
 *
 * OCP: adding a new ``EventKind`` requires:
 *   1. A new member in the ``EventKind`` union below.
 *   2. A new payload type added to ``EventPayload``.
 *   3. (If it contributes to a counter) A new line in
 *      ``incrementCounters`` — one case, not a switch-across-files.
 *   4. A new SSE mapping in ``sseAdapter.ts`` (separate file).
 *   NO changes to component render logic — views iterate entries.
 *
 * # DECISION: ``bySection`` and ``counters`` are denormalized and
 * #   recomputed on each append, not derived at read time.
 * # WHY: Spine nodes re-render on every append (new counter value);
 * #      recomputing the full map per render is O(N) per frame, which
 * #      at 1000s of events late-run makes the spine janky. Appending
 * #      into a mutable-looking-but-structurally-shared map is O(1)
 * #      and keeps reads O(1) per section.
 * # DATE: 2026-04-21
 *
 * # DECISION: unknown kinds fail silently (warn + drop) rather than
 * #   throw.
 * # WHY: a malformed or future-version event from the backend must
 * #      not crash a writer's task pane. The warn surfaces the bug to
 * #      devs (same posture as ``state.ts``'s logUnexpected); the
 * #      drop preserves the log's strict-enum invariant for readers.
 * # DATE: 2026-04-21
 */

// ---------------------------------------------------------------------------
// Kinds — closed enum. Add here + EventPayload + incrementCounters (if
// relevant) when extending.
// ---------------------------------------------------------------------------

export const EVENT_KINDS = [
  "draft", // asthra drafted a section
  "regen", // asthra regenerated a section (accepted or rejected)
  "cite", // citation swap
  "web", // web search
  "prompt", // writer sent a prompt
  "askwhy", // writer asked why
  "word", // writer manually edited in Word
  "library", // library contents changed
  "approval", // writer made an approval decision
  "run_start", // Phase 8.5 — run lifecycle bracket: drafting started
  "run_complete", // Phase 8.5 — run lifecycle bracket: drafting finished
  // Phase 9.5 (Bundle A) — pre-run conversational selections that
  // previously happened silently in modals. Surfaced in chat as a
  // {writer-said, asthra-acked} tuple so the chat is the audit
  // trail for these decisions too. See messageProjection for the
  // tuple expansion.
  "doctype_selected",
  "library_selected",
  // Phase 9.5 (Bundle B+D) — Asthra-emitted unprompted prose
  // (the idle greeting, refusals, stop notices). Distinct from
  // ``askwhy`` (which carries a writer question) and from
  // ``draft``/``regen`` (which describe completed work).
  "agent_note",
  "audit", // regulatory audit progress / result for a section
  // Intent-routed chat: ad-hoc draft proposal for a section (held
  // content awaiting the writer's Insert — SSOT untouched until then).
  "adhoc_draft",
  // Intent-routed chat: decided edit-highlight proposal. Appended
  // AFTER the writer approves/rejects (the pending proposal is
  // transient hook state) so the drafted output + outcome persist in
  // the thread. Entries are immutable — decision is baked in.
  "edit_proposal",
  // One completed (or failed) tool step from a chat turn's tool loop.
  // Appended when the step RESOLVES — the in-flight "running" box is
  // transient bridge state (useChatBridge.liveSteps), never logged, so
  // the append-only invariant holds and history can't show a frozen
  // spinner. Renders as a persistent activity row in the thread.
  "chat_tool_step",
  // One line of user or agent activity (ran a review, fixed flags, loaded
  // the document, …) that is neither conversation nor a run bracket. The
  // entry's ``actor`` says who did it; renders as an activity row.
  "activity",
] as const;

export type EventKind = (typeof EVENT_KINDS)[number];

/** One entry in a library-event's diff list. ``op`` describes the
 *  change since the prior snapshot; ``name`` is the document name
 *  the writer recognises. Spec 24 T2. */
export interface LibraryDiffEntry {
  op: "added" | "removed" | "renamed";
  name: string;
  /** When op === "renamed", the prior name. Undefined otherwise. */
  fromName?: string;
}

/**
 * Discriminated payload union — each kind carries the fields its
 * Timeline-view row needs to render without having to fetch anything
 * extra. Kept narrow on purpose; add fields only when a view needs
 * them. YAGNI check: if no view reads it, leave it out.
 */
export type EventPayload =
  | { kind: "draft"; wordCount?: number; content?: string }
  | {
      kind: "adhoc_draft";
      /** Insert-ready proposal content (sanitized before Word insert). */
      content: string;
      /** The writer's chat instruction that produced this draft. */
      instruction?: string;
      wordCount?: number;
      /** Backend module id — Insert targets this section's SSOT. */
      moduleId?: string;
      /** "redraft" = full-section redraft proposal (/regen command);
       *  absent/"draft" = ad-hoc draft fragment. Drives the decided
       *  projection's header + intent chip. */
      mode?: "draft" | "redraft";
      /** Decided Approve/Reject proposal (new flow): appended AFTER the
       *  writer decides, outcome baked in — mirrors ``edit_proposal``.
       *  Absent on legacy entries, which keep the Insert-bubble
       *  rendering (messageProjection branches on this). */
      decision?: "accepted" | "rejected";
      /** Apply-path caveats, e.g. "couldn't update §9.2 in the
       *  document; use Load to refresh". */
      outcomeNote?: string;
    }
  | {
      // Decided edit-highlight proposal (chat "edit highlight"
      // intent). Written on Approve/Reject with the outcome baked in;
      // the pending state never touches the log (transient hook
      // state), so restored history can't resurrect live buttons.
      kind: "edit_proposal";
      /** The writer's chat instruction that produced the rewrite. */
      instruction: string;
      /** The highlighted text as it stood at proposal time. */
      originalText: string;
      /** Insert-ready replacement (document grammar markdown). */
      replacementText: string;
      decision: "accepted" | "rejected";
      /** e.g. "the text appeared more than once; the first occurrence
       *  was updated" — apply-path caveats from approve(). */
      outcomeNote?: string;
    }
  | {
      kind: "regen";
      attempt: number;
      outcome: "accepted" | "rejected" | "submitted";
      wordCount?: number;
      /** Optional writer-supplied guidance captured by RegenOne.
       *  v1 is log-local only — not threaded to the backend. */
      guidance?: string | null;
    }
  | { kind: "cite"; from?: string; to?: string }
  | { kind: "web"; query: string }
  | {
      kind: "prompt";
      text: string;
      selectionContext?: string;
      quotedMessage?: import("./messageProjection").QuotedMessage;
      /** Intent classified at submit (routing on unless the
       *  chat_intent_routing_disabled kill switch is set).
       *  Captured at write time so the chat chip survives reloads.
       *  Absent when routing is off or classification was skipped. */
      intent?: import("../api/client").ChatRoutedIntent;
      /** @-mentioned source sections/tables, in citation order (index + 1 ==
       *  the ``@[n]`` marker number in ``text``). Persisted so the transcript
       *  can render each marker as an asset chip (icon + name) on reload.
       *  Absent when the message tagged nothing. */
      attachments?: import("../types").MentionAttachment[];
    }
  | {
      kind: "askwhy";
      question: string;
      answer?: string;
      /** Intent the question was routed under — carried on the answer
       *  so the assistant bubble's chip survives reload. */
      intent?: import("../api/client").ChatRoutedIntent;
      /** Edits the agent made this turn (doc_propose_changes) — the
       *  reply's C-id links, diff popover and Undo / Redo. */
      changes?: import("./services/chatChanges").ChatChange[];
    }
  | {
      // Resolved chat tool step (see EVENT_KINDS note). ``tool`` is the
      // backend tool id (display name resolved at projection time via
      // the toolDisplayNames SSOT); ``stepId`` disambiguates steps
      // within one turn for tests/telemetry.
      kind: "chat_tool_step";
      tool: string;
      stepId: string;
      status: "done" | "error";
      /** One-line writer-facing summary of what the step did (query,
       *  section number, …) captured at start time. */
      inputSummary?: string;
      /** Truncated result preview from the backend, when sent. */
      resultPreview?: string;
      /** The tool's main argument as the stream reported it, untruncated
       *  (e.g. ``find_impacted_sections``' ``document``). */
      toolQuery?: string;
      /** What came back, in one line ("4 matches · §3.2.P.5.1"). */
      resultSummary?: string;
      /** What came back, a few lines the row opens to. */
      resultDetail?: string;
      durationMs?: number;
    }
  | {
      kind: "word";
      paragraphCount?: number;
      /** Spec 24 T4 — short text excerpt anchored on the diff
       *  between prior and next document body. Captured by
       *  useWordEdits; null when no usable diff (first observation
       *  / identical / Office.js absent). */
      snippet?: string;
    }
  | {
      kind: "library";
      fromVersion?: string;
      toVersion?: string;
      /** Spec 24 T2 — per-document diff between the two snapshots,
       *  computed by the producer (refreshLibraryInventory in
       *  VariantCShell). Optional because the first observation
       *  has no prior to compare against. */
      diff?: ReadonlyArray<LibraryDiffEntry>;
    }
  | {
      kind: "approval";
      decision: "accepted" | "rejected" | "edited";
      note?: string;
      /** The decision already shows as the footer on its proposal bubble
       *  (edit highlight, ad-hoc draft); the chat skips the system note so
       *  the outcome isn't said twice. The entry stays for the audit log. */
      shownOnBubble?: boolean;
    }
  | {
      // Phase 8.5 — run lifecycle bracket. Emitted by the shell at
      // RUN_STARTED dispatch sites so the chat surface (and persisted
      // chat history) can render "Run started" as a system note.
      // Carries no payload fields — the entry's ``ts`` + ``runId``
      // already capture all the writer-facing detail.
      kind: "run_start";
    }
  | {
      // Phase 8.5 — pairs with ``run_start``. Carries the
      // RunSummary fields the chat note surfaces (drafted/total,
      // flagged count, duration) so the writer sees the run's
      // outcome inline. Optional because the shell's dispatch site
      // may emit before summary stats are computed in test harnesses.
      kind: "run_complete";
      totalSections?: number;
      draftedSections?: number;
      flaggedSections?: number;
      durationMs?: number;
    }
  | {
      // Phase 9.5 (Bundle A) — writer picked a doctype via the
      // canonical DoctypePicker surface. ``label`` is the
      // writer-facing name (e.g. "CSR — Clinical Study Report")
      // captured at write time so the chat row stays readable
      // regardless of catalog drift. ``docCode`` is retained for
      // future filtering / replay; not surfaced to the writer.
      kind: "doctype_selected";
      docCode: string;
      label: string;
    }
  | {
      // Phase 9.5 (Bundle A) — writer picked or just created a
      // library. ``libraryName`` is captured at write time for the
      // same reason as ``doctype_selected.label``. ``created`` is
      // true when this event corresponds to a fresh library being
      // created (vs picking an existing one) so the chat copy can
      // distinguish the two ("Created" vs "Selected").
      kind: "library_selected";
      libraryId: string;
      libraryName: string;
      created?: boolean;
    }
  | {
      // Phase 9.5 (Bundle B+D) — Asthra-emitted unprompted prose.
      // ``commandId`` (when present) tags a note so it can be found
      // again (e.g. the persisted greeting's sentinel). ``text`` is
      // the rendered prose so the projection stays a pure
      // pass-through.
      kind: "agent_note";
      text: string;
      commandId?: string;
    }
  | {
      // Regulatory audit progress / result for a section. ``phase``
      // tracks the three-step flow (started → guidelines → precedents →
      // complete); ``message`` is the human-facing line; ``requirementCount``
      // and ``status`` are populated on completion.
      kind: "audit";
      phase: "started" | "guidelines" | "context" | "precedents" | "complete" | "error";
      message: string;
      requirementCount?: number;
      status?: string;
      /** Flags the finished review left in the section (complete only). */
      regulatorFlagCount?: number;
    }
  | {
      // A line of user or agent activity — see EVENT_KINDS. ``text`` is
      // the finished sentence ("Ran a regulatory review on §2.6.6.").
      kind: "activity";
      text: string;
    };

// ---------------------------------------------------------------------------
// Entry + log shape
// ---------------------------------------------------------------------------

export interface EventLogEntry {
  /** Stable id. Format: ``${kind}-${seq}`` — monotonic seq per log. */
  id: string;
  /** Wall-clock timestamp (epoch ms). */
  ts: number;
  /** Who caused the event. Writers vs agents — surfaced distinctly in Timeline. */
  actor: "asthra" | "you";
  /** Closed-enum discriminator; matches ``payload.kind``. */
  kind: EventKind;
  /** Optional — section this event is scoped to. null = run-wide.
   *  This is the STABLE IDENTIFIER (e.g. backend ``module_id``
   *  ``"FRM-607"``); use it for filtering, lookups, equality. */
  sectionId: string | null;
  /** Optional — writer-facing label for the section, captured at
   *  the write boundary (typically ``§${number}`` from the section
   *  title lookup). Used by the chat surface, timeline title, and
   *  EntryDetailDrawer header so writers never see backend
   *  identifiers like ``FRM-607``. Null when the producer can't
   *  resolve a label (no section context, or lookup unavailable);
   *  consumers fall back to ``sectionId`` then to "section".
   *
   *  # DECISION: stored alongside ``sectionId`` rather than derived
   *  #   at render time.
   *  # WHY: chat history persists in localStorage — render-time
   *  #      lookups would lose context across reloads (the section
   *  #      list isn't durable, runs come and go). Capturing the
   *  #      label at write time means past chats stay readable
   *  #      indefinitely. Pure-projection invariant preserved
   *  #      (messageProjection has no React/sections dependency).
   *
   *  Optional in the type so legacy persisted entries (pre-Phase-9
   *  localStorage) deserialise without a migration; appendEvent
   *  normalises every newly-written entry to ``string | null``. */
  sectionLabel?: string | null;
  /** Optional run id for cross-run audit exports. Null in single-run tests. */
  runId: string | null;
  /** Kind-discriminated payload. */
  payload: EventPayload;
}

export interface SpineCounters {
  webSearches: number;
  askWhys: number;
  librarySwaps: number;
  regens: number;
  /** Spec 23 P4 — citation swap events. Future cite producer
   *  (pending a backend citation_swap signal per Spec 23 §5 Q5)
   *  advances this. Zero today until the producer lands. */
  citationSwaps: number;
  /** Spec 23 P4 — writer Word-edit events. Advanced by the word
   *  producer (Spec 23 P2.3 shipped) on every detected edit moment. */
  wordEdits: number;
}

export interface EventLog {
  /** All entries, append-order. Immutable array — replace, never mutate. */
  entries: readonly EventLogEntry[];
  /** section id → that section's entries, in insertion order. */
  bySection: ReadonlyMap<string, readonly EventLogEntry[]>;
  /** section id → Spine-badge counters for that section. */
  counters: ReadonlyMap<string, SpineCounters>;
  /** Monotonic sequence for stable ids; increments on every append. */
  nextSeq: number;
}

// ---------------------------------------------------------------------------
// Factory + update
// ---------------------------------------------------------------------------

export function emptyEventLog(): EventLog {
  return {
    entries: [],
    bySection: new Map(),
    counters: new Map(),
    nextSeq: 0,
  };
}

/**
 * Input shape the adapter provides — the log fills in ``id`` from its
 * own seq so callers don't need to generate it.
 */
export interface AppendInput {
  ts: number;
  actor: "asthra" | "you";
  sectionId: string | null;
  /** Writer-facing label paired with ``sectionId`` — see EventLogEntry
   *  for the decision rationale. Optional in the input shape so legacy
   *  callers stay valid; appendEvent normalises missing → null. */
  sectionLabel?: string | null;
  runId: string | null;
  payload: EventPayload;
}

/**
 * Append one event. Returns a NEW log (input untouched).
 *
 * If ``input.payload.kind`` isn't in ``EVENT_KINDS`` at runtime
 * (shouldn't happen with TS on, but can if the adapter's source is
 * JSON from a future backend), the append is a no-op and a warning
 * is logged — we never crash the pane on a dodgy event.
 */
export function appendEvent(log: EventLog, input: AppendInput): EventLog {
  const kind = input.payload.kind;
  if (!isKnownKind(kind)) {
    logUnknownKind(kind, input);
    return log;
  }

  const entry: EventLogEntry = {
    id: `${kind}-${log.nextSeq}`,
    ts: input.ts,
    actor: input.actor,
    kind,
    sectionId: input.sectionId,
    sectionLabel: input.sectionLabel ?? null,
    runId: input.runId,
    payload: input.payload,
  };

  const entries = [...log.entries, entry];
  const bySection = updateBySection(log.bySection, entry);
  const counters = input.sectionId ? updateCounters(log.counters, input.sectionId, entry) : log.counters;

  return {
    entries,
    bySection,
    counters,
    nextSeq: log.nextSeq + 1,
  };
}

// ---------------------------------------------------------------------------
// Pure helpers — testable in isolation
// ---------------------------------------------------------------------------

function isKnownKind(value: string): value is EventKind {
  return (EVENT_KINDS as readonly string[]).includes(value);
}

function logUnknownKind(kind: string, input: AppendInput): void {
  if (typeof console !== "undefined") {
    console.warn("[EventLog] dropping event with unknown kind", {
      kind,
      input,
    });
  }
}

function updateBySection(
  prev: ReadonlyMap<string, readonly EventLogEntry[]>,
  entry: EventLogEntry,
): ReadonlyMap<string, readonly EventLogEntry[]> {
  if (!entry.sectionId) return prev;
  const existing = prev.get(entry.sectionId) ?? [];
  const next = new Map(prev);
  next.set(entry.sectionId, [...existing, entry]);
  return next;
}

/**
 * Per-section counter update. Only kinds that contribute to the
 * Spine badges advance a counter; the rest are recorded in ``entries``
 * but don't bump anything.
 *
 * Adding a new badge = one line here + one badge-render case in the
 * Spine (P3). No other files touched.
 */
function updateCounters(
  prev: ReadonlyMap<string, SpineCounters>,
  sectionId: string,
  entry: EventLogEntry,
): ReadonlyMap<string, SpineCounters> {
  const current = prev.get(sectionId) ?? emptyCounters();
  let next: SpineCounters | null = null;
  switch (entry.kind) {
    case "web":
      next = { ...current, webSearches: current.webSearches + 1 };
      break;
    case "askwhy":
      next = { ...current, askWhys: current.askWhys + 1 };
      break;
    case "library":
      next = { ...current, librarySwaps: current.librarySwaps + 1 };
      break;
    case "regen":
      next = { ...current, regens: current.regens + 1 };
      break;
    case "cite":
      next = { ...current, citationSwaps: current.citationSwaps + 1 };
      break;
    case "word":
      next = { ...current, wordEdits: current.wordEdits + 1 };
      break;
    // draft, prompt, approval, run brackets, pre-run picks:
    // logged but don't badge any section.
    case "draft":
    case "adhoc_draft":
    case "edit_proposal":
    case "prompt":
    case "approval":
    case "run_start":
    case "run_complete":
    case "doctype_selected":
    case "library_selected":
    case "agent_note":
    case "audit":
    case "chat_tool_step":
    case "activity":
      return prev; // no counter change — return same map (cheap)
    default:
      // Exhaustive check — a new kind without a case here fails to compile.
      return assertUnreachableKind(entry.kind);
  }
  const out = new Map(prev);
  out.set(sectionId, next);
  return out;
}

function emptyCounters(): SpineCounters {
  return {
    webSearches: 0,
    askWhys: 0,
    librarySwaps: 0,
    regens: 0,
    citationSwaps: 0,
    wordEdits: 0,
  };
}

function assertUnreachableKind(value: never): ReadonlyMap<string, SpineCounters> {
  if (typeof console !== "undefined") {
    console.warn("[EventLog] unhandled kind in counter update", value);
  }
  // Never reached in practice; keep the types honest.
  return new Map();
}

// ---------------------------------------------------------------------------
// Query helpers — used by P4 Timeline view + P5 filters
// ---------------------------------------------------------------------------

/** Entries in reverse-chronological order. Useful for Timeline view. */
export function entriesNewestFirst(log: EventLog): readonly EventLogEntry[] {
  // Work on a copy so log.entries stays untouched.
  return [...log.entries].reverse();
}

/** Entries scoped to one section, preserving insertion order. */
export function entriesForSection(log: EventLog, sectionId: string): readonly EventLogEntry[] {
  return log.bySection.get(sectionId) ?? [];
}

/** Counters for a section, zeros if absent. */
export function countersForSection(log: EventLog, sectionId: string): SpineCounters {
  return log.counters.get(sectionId) ?? emptyCounters();
}
